/**
 * Personalakte (/mitarbeiter/:id): Kopf mit Live-Status-Badges + Tabs
 * Stammdaten / Kontaktdaten / Stellen / Abwesenheiten / Beauftragungen /
 * Vorsorge / App-Freigaben / Konto.
 */

import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { ArrowLeft } from "lucide-react"
import { Link, useParams } from "react-router-dom"
import { AccountTab } from "@/components/employees/AccountTab"
import { ApprovalsTab } from "@/components/employees/ApprovalsTab"
import { AssignmentsTab } from "@/components/employees/AssignmentsTab"
import { AssignmentBadge, EmployeeStatusBadge } from "@/components/employees/badges"
import { ContactTab } from "@/components/employees/ContactTab"
import { EmployeeAbsencesTab } from "@/components/employees/EmployeeAbsencesTab"
import { ExaminationsTab } from "@/components/employees/ExaminationsTab"
import { PositionsTab } from "@/components/employees/PositionsTab"
import { StammdatenTab } from "@/components/employees/StammdatenTab"
import { LoadingSpinner } from "@/components/shared"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useCan } from "@/lib/permissions"
import { api, apiError } from "@/services/api"

export function EmployeeDetailPage() {
  const { id = "" } = useParams<{ id: string }>()
  const canWrite = useCan("personal_schreiben")
  const [tab, setTab] = useState("stammdaten")

  const query = useQuery({
    queryKey: ["employee", id, "detail"],
    queryFn: () => api.employees.get(id),
    enabled: id !== "",
  })

  if (query.isLoading) return <LoadingSpinner fullPage />

  if (query.isError || !query.data) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" asChild>
          <Link to="/mitarbeiter">
            <ArrowLeft /> Zurück zum Verzeichnis
          </Link>
        </Button>
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            {query.error ? apiError(query.error) : "Mitarbeiter nicht gefunden."}
          </CardContent>
        </Card>
      </div>
    )
  }

  const detail = query.data

  return (
    <div className="space-y-4">
      {/* Kopf */}
      <div className="space-y-2">
        <Button variant="ghost" size="sm" asChild className="-ml-2 text-muted-foreground">
          <Link to="/mitarbeiter">
            <ArrowLeft /> Mitarbeiterverzeichnis
          </Link>
        </Button>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">{detail.displayName}</h1>
          <EmployeeStatusBadge employee={detail} />
          <AssignmentBadge status={detail.assignmentStatus} />
          {detail.username && (
            <span className="text-sm text-muted-foreground">@{detail.username}</span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="stammdaten">Stammdaten</TabsTrigger>
          <TabsTrigger value="kontakt">
            Kontaktdaten (
            {detail.counters.phoneNumbers + detail.counters.emails + detail.counters.addresses})
          </TabsTrigger>
          <TabsTrigger value="stellen">Stellen ({detail.counters.positions})</TabsTrigger>
          <TabsTrigger value="abwesenheiten">
            Abwesenheiten ({detail.counters.absences})
          </TabsTrigger>
          <TabsTrigger value="beauftragungen">
            Beauftragungen ({detail.counters.assignments})
          </TabsTrigger>
          <TabsTrigger value="vorsorge">
            Vorsorge ({detail.counters.examinationRecords})
          </TabsTrigger>
          <TabsTrigger value="freigaben">App-Freigaben</TabsTrigger>
          <TabsTrigger value="konto">Konto</TabsTrigger>
        </TabsList>
        <TabsContent value="stammdaten" className="mt-4">
          <StammdatenTab key={detail.id} detail={detail} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="kontakt" className="mt-4">
          <ContactTab employeeId={detail.id} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="stellen" className="mt-4">
          <PositionsTab employeeId={detail.id} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="abwesenheiten" className="mt-4">
          <EmployeeAbsencesTab employeeId={detail.id} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="beauftragungen" className="mt-4">
          <AssignmentsTab employeeId={detail.id} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="vorsorge" className="mt-4">
          <ExaminationsTab employeeId={detail.id} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="freigaben" className="mt-4">
          <ApprovalsTab detail={detail} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="konto" className="mt-4">
          <AccountTab detail={detail} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
